import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import "@/App.css";

import Landing from "@/pages/Landing";
import Intro from "@/pages/Intro";
import Auth from "@/pages/Auth";
import Onboarding from "@/pages/Onboarding";
import Home from "@/pages/Home";
import Results from "@/pages/Results";
import RestaurantDetail from "@/pages/RestaurantDetail";
import CuratedMenu from "@/pages/CuratedMenu";
import Checkout from "@/pages/Checkout";
import PackageSelection from "@/pages/PackageSelection";
import Confirmation from "@/pages/Confirmation";
import MyBookings from "@/pages/MyBookings";
import Approvals from "@/pages/Approvals";
import Profile from "@/pages/Profile";
import PrintBooking from "@/pages/PrintBooking";
import LegalFooterLinks, { PrivacyPolicy, Terms, RefundPolicy } from "@/pages/Legal";

import PartnerInbox from "@/pages/partner/PartnerInbox";
import PartnerCalendar from "@/pages/partner/PartnerCalendar";
import PartnerOnboarding from "@/pages/partner/PartnerOnboarding";
import PartnerPackages from "@/pages/partner/PartnerPackages";
import PartnerMenu from "@/pages/partner/PartnerMenu";
import PartnerSettings from "@/pages/partner/PartnerSettings";

import AdminOps from "@/pages/admin/AdminOps";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminRestaurants from "@/pages/admin/AdminRestaurants";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminAudit from "@/pages/admin/AdminAudit";

import BookerLayout from "@/components/BookerLayout";
import PartnerLayout from "@/components/PartnerLayout";
import AdminLayout from "@/components/AdminLayout";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center text-light-ink">
      <span className="animate-pulse">Loading Tablo…</span>
    </div>
  );
}

function RequireAuth({ role, children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) {
    const target = role === "admin" ? "/admin/login" : role === "restaurant" ? "/partner/auth" : "/auth";
    return <Navigate to={target} replace />;
  }
  if (role && user.role !== role) {
    const target = user.role === "admin" ? "/admin" : user.role === "restaurant" ? "/partner/inbox" : "/home";
    return <Navigate to={target} replace />;
  }
  return children;
}

function BookerShell({ children, requireOnboarded = true }) {
  const { user } = useAuth();
  if (requireOnboarded && user && !user.onboarded) return <Navigate to="/onboarding" replace />;
  return <BookerLayout>{children}</BookerLayout>;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <Routes>
          {/* Public */}
          <Route path="/" element={<Landing />} />
          <Route path="/intro" element={<Intro />} />
          <Route path="/auth" element={<Auth role="booker" />} />
          <Route path="/partner/auth" element={<Auth role="restaurant" />} />
          <Route path="/admin/login" element={<Auth role="admin" />} />

          {/* Booker */}
          <Route path="/onboarding" element={
            <RequireAuth role="booker"><Onboarding /></RequireAuth>
          } />
          <Route path="/home" element={
            <RequireAuth role="booker"><BookerShell><Home /></BookerShell></RequireAuth>
          } />
          <Route path="/results" element={
            <RequireAuth role="booker"><BookerShell><Results /></BookerShell></RequireAuth>
          } />
          <Route path="/restaurant/:id" element={
            <RequireAuth role="booker"><BookerShell><RestaurantDetail /></BookerShell></RequireAuth>
          } />
          <Route path="/curated-menu/:bookingId" element={
            <RequireAuth role="booker"><BookerShell><CuratedMenu /></BookerShell></RequireAuth>
          } />
          <Route path="/checkout/:bookingId" element={
            <RequireAuth role="booker"><BookerShell><Checkout /></BookerShell></RequireAuth>
          } />
          <Route path="/package-selection/:bookingId" element={
            <RequireAuth role="booker"><BookerShell><PackageSelection /></BookerShell></RequireAuth>
          } />
          <Route path="/confirmation/:bookingId" element={
            <RequireAuth role="booker"><BookerShell><Confirmation /></BookerShell></RequireAuth>
          } />
          <Route path="/my-bookings" element={
            <RequireAuth role="booker"><BookerShell><MyBookings /></BookerShell></RequireAuth>
          } />
          <Route path="/approvals" element={
            <RequireAuth role="booker"><BookerShell><Approvals /></BookerShell></RequireAuth>
          } />
          <Route path="/profile" element={
            <RequireAuth role="booker"><BookerShell><Profile /></BookerShell></RequireAuth>
          } />

          {/* Partner */}
          <Route path="/partner/onboarding" element={
            <RequireAuth role="restaurant"><PartnerLayout><PartnerOnboarding /></PartnerLayout></RequireAuth>
          } />
          <Route path="/partner/inbox" element={
            <RequireAuth role="restaurant"><PartnerLayout><PartnerInbox /></PartnerLayout></RequireAuth>
          } />
          <Route path="/partner/calendar" element={
            <RequireAuth role="restaurant"><PartnerLayout><PartnerCalendar /></PartnerLayout></RequireAuth>
          } />
          <Route path="/partner/packages" element={
            <RequireAuth role="restaurant"><PartnerLayout><PartnerPackages /></PartnerLayout></RequireAuth>
          } />
          <Route path="/partner/menu" element={
            <RequireAuth role="restaurant"><PartnerLayout><PartnerMenu /></PartnerLayout></RequireAuth>
          } />
          <Route path="/partner/settings" element={
            <RequireAuth role="restaurant"><PartnerLayout><PartnerSettings /></PartnerLayout></RequireAuth>
          } />

          {/* Admin */}
          <Route path="/admin" element={
            <RequireAuth role="admin"><AdminLayout><AdminOps /></AdminLayout></RequireAuth>
          } />
          <Route path="/admin/bookings" element={
            <RequireAuth role="admin"><AdminLayout><AdminBookings /></AdminLayout></RequireAuth>
          } />
          <Route path="/admin/restaurants" element={
            <RequireAuth role="admin"><AdminLayout><AdminRestaurants /></AdminLayout></RequireAuth>
          } />
          <Route path="/admin/users" element={
            <RequireAuth role="admin"><AdminLayout><AdminUsers /></AdminLayout></RequireAuth>
          } />
          <Route path="/admin/audit" element={
            <RequireAuth role="admin"><AdminLayout><AdminAudit /></AdminLayout></RequireAuth>
          } />

          {/* Print view — works for booker, restaurant, and admin (auth required) */}
          <Route path="/print/:bid" element={
            <RequireAuth><PrintBooking /></RequireAuth>
          } />

          {/* Legal — public */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/refunds" element={<RefundPolicy />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <LegalFooterLinks />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
